/**
 *
 * PagiQueryTextField
 *
 */
import React, { CSSProperties, memo } from 'react';
import { paginationActions, usePaginationSlice } from '../Pagination/slice';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import { Box } from '@mui/material';
import { Formik, Field } from 'formik';
import { FormTextControl } from '@assetic/web-components';
import { User, Portfolio, Subscription, Company } from 'types/models';
import { PaginationType } from '../Pagination/slice/types';
import { makeThemeStyle, NestedCssProperties } from 'styles/makeThemeStyle';

export type SimpleFilterFieldsType = keyof (User &
  Portfolio &
  Subscription &
  Company);
export type ComplexFilterFieldsType = (
  | SimpleFilterFieldsType
  | SimpleFilterFieldsType[]
)[];

interface StyleProps {
  overrideTextFieldStyle?: NestedCssProperties<CSSProperties>;
}

interface Props extends StyleProps {
  filterFields: ComplexFilterFieldsType;
  path: string;
  label: string;
  preFilter?: string;
  helperText?: React.ReactNode;
  paginationType: PaginationType;
}

const useStyles = makeThemeStyle<StyleProps>(theme => ({
  queryTextField: (props?: StyleProps) => ({
    alignSelf: 'flex-end',
    width: 300,
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      float: 'unset',
      width: '100%',
    },
    '& label': {
      width: '90%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      lineHeight: '18px',
    },
    '& span': {
      width: 'max-content',
    },
    ...props?.overrideTextFieldStyle,
  }),
}));

/**
 * For one level filter, fields will look like @param ['field_1', 'field_2'] @returns ...field_1|field_2...
 * For nested filter, fields will look like @param ['field', ['nested_root', 'nested_child']] @returns ...field|nested_root.nested_child....
 */
export const pagiFilterFieldsGen = (
  fields: ComplexFilterFieldsType,
  query: string,
  preFilter?: string,
): string => {
  if (fields.length === 0) {
    return '';
  }

  (fields as (string | string[])[]).forEach((field, index, thisFields) => {
    if (Array.isArray(field)) {
      thisFields[index] = field.join('.');
    }
  });

  let filter = `(${_.uniq(fields).join('|')})@=*${encodeURIComponent(query)}`;
  if (preFilter) {
    filter += `,${encodeURIComponent(preFilter)}`;
  }

  return filter;
};

export const PagiQueryTextField = memo((props: Props) => {
  const {
    filterFields,
    path,
    label,
    preFilter,
    paginationType,
    helperText,
    ...styleProps
  } = props;
  const dispatch = useDispatch();
  const classes = useStyles(styleProps);
  usePaginationSlice();

  const handleClick = (query: string) => {
    dispatch(
      paginationActions.updateQueryInfo({
        filters: pagiFilterFieldsGen(filterFields, query, preFilter),
        sorts: '',
        page: 0,
        page_size: 12,
        path: path,
        pagiType: paginationType,
      }),
    );
  };

  const handleOnChange = _.debounce(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleClick(e.target.value);
    },
    500,
  );

  return (
    <>
      <Box className={classes.queryTextField}>
        <Formik
          initialValues={{ query: '' }}
          onSubmit={values => handleClick(values.query)}
        >
          {formprops => (
            <form onSubmit={formprops.handleSubmit}>
              <Field
                component={FormTextControl}
                name="query"
                label={label}
                onChange={handleOnChange}
                size="small"
                helperText={helperText}
              />
            </form>
          )}
        </Formik>
      </Box>
    </>
  );
});
